import { ACTIVE_CASES_PAGE_SIZE } from '../utils/casesListUrl';
import { mvpApi } from './api';

/** v1 first letter pool (excludes B, I, O, Q, S, Z). */
const PUBLIC_CASE_LETTERS = 'ACDEFGHJKLMNPRTUVWXY';
/** v2: any two A–Z letters + hyphen + five digits (e.g. SR-12345). */
const PUBLIC_CASE_V2_RE = /^[A-Za-z]{2}-\d{5}$/;
/** v1 legacy: one letter + five digits (e.g. A74327). */
const PUBLIC_CASE_LEGACY_RE = new RegExp(`^[${PUBLIC_CASE_LETTERS}]\\d{5}$`, 'i');

/**
 * Parse `/case/:caseId` route param.
 * - Legacy: `{userId}__{sessionUuid}`
 * - Public v2: two letters + hyphen + five digits (e.g. SR-12345)
 * - Public v1: one letter + five digits (e.g. A74327) until migrated
 */
export function parseCaseRouteParam(caseId) {
  const raw = String(caseId || '').trim();
  if (!raw) return { mode: 'invalid' };
  if (raw.includes('__')) {
    const [userId, ...rest] = raw.split('__');
    const sessionId = rest.join('__');
    if (userId && sessionId) return { mode: 'legacy', userId, sessionId };
    return { mode: 'invalid' };
  }
  if (PUBLIC_CASE_V2_RE.test(raw)) {
    const u = raw.toUpperCase();
    return { mode: 'public', publicCaseId: `${u.slice(0, 2)}-${u.slice(3, 8)}` };
  }
  if (PUBLIC_CASE_LEGACY_RE.test(raw)) {
    const letter = raw[0].toUpperCase();
    const digits = raw.slice(1);
    return { mode: 'public', publicCaseId: `${letter}${digits}` };
  }
  return { mode: 'invalid' };
}

/** Build customer-facing case id for links (prefer API `public_case_id`). */
export function toCaseId(userId, sessionId, publicCaseId = null) {
  if (publicCaseId && String(publicCaseId).trim()) {
    const spec = parseCaseRouteParam(String(publicCaseId).trim());
    if (spec.mode === 'public') return spec.publicCaseId;
  }
  return `${userId}__${sessionId}`;
}

/** @deprecated Use parseCaseRouteParam + resolve API for public ids */
export function fromCaseId(caseId) {
  const spec = parseCaseRouteParam(caseId);
  if (spec.mode === 'legacy') return { userId: spec.userId, sessionId: spec.sessionId };
  return { userId: '', sessionId: '' };
}

export async function loadAdvisorEssentialData(options = {}) {
  const casesParams = options.casesParams ?? { page: 1, page_size: ACTIVE_CASES_PAGE_SIZE };
  const [casesRes, meRes, advisorsRes] = await Promise.all([
    mvpApi.getAllUserCases(casesParams),
    mvpApi.getMe(),
    mvpApi.getAdminAdvisors().catch(() => ({ data: { advisors: [] } })),
  ]);
  const cases = casesRes.data?.cases || [];
  const me = meRes.data?.user || meRes.data || {};
  const advisors = advisorsRes.data?.advisors || [];
  const casesMeta = {
    total_cases: casesRes.data?.total_cases,
    page: casesRes.data?.page,
    page_size: casesRes.data?.page_size,
    total_pages: casesRes.data?.total_pages,
    summary: casesRes.data?.summary,
  };
  return { cases, me, advisors, casesMeta };
}

export async function loadAdvisorDirectoryData(params = {}) {
  const usersRes = await mvpApi.getAdminRegistrations(params);
  const data = usersRes.data || {};
  return {
    users: data.users || [],
    total: data.total ?? 0,
    page: data.page ?? 1,
    page_size: data.page_size ?? params.page_size,
    total_pages: data.total_pages ?? 0,
  };
}

/** Loads cases + profile; optionally loads directory in parallel. */
export async function loadAdvisorData(options = {}) {
  const { casesParams, includeDirectory = true } = options;
  const essentialPromise = loadAdvisorEssentialData(
    casesParams ? { casesParams } : {},
  );
  if (!includeDirectory) {
    return essentialPromise;
  }
  const [essential, directory] = await Promise.all([
    essentialPromise,
    loadAdvisorDirectoryData({ page: 1, page_size: 500 }),
  ]);
  return { ...essential, users: directory.users };
}
